/* iceberg-ui */
import { Box } from '@auviknetworks/iceberg-ui';

/* MUI */
import { CircularProgress, Paper } from '@mui/material';

/* Shared */
import { UserBubbles } from 'application/Shared/Components/Avatars';
import { StringCell } from 'application/Shared/Components/Cells';
import {
    Header,
    ManagedTableV2ContextProvider,
    ManagedTableV2UI
} from 'application/Shared/Components/Table';
import { ColumnDef } from 'application/Shared/Components/Table/types';
import { TooltipPlacement } from 'application/Shared/Components/Tooltips';
import { TABLENAMES } from 'application/Shared/Constants';
import { AppLog } from 'application/Shared/types/appLog';
import { Metadata } from 'application/Shared/types/metadata';
import { User } from 'application/Shared/types/user';

/* Hooks */
import { useFetchTenantApplicationLogs } from 'hooks/dataAccess/Tenant/ApplicationLogs/tenantApplicationLogs';

/*  React  */
import React, { useMemo } from 'react';
import { useParams } from 'react-router';

interface AppLogAugmented extends AppLog {
    count: number;
    logs: AppLog[];
    url: string;
}

const UsersCell = ({ row }: { row: AppLogAugmented }) => {
    const { logs } = row;
    const uniqueUsersMap: { [key: string]: { user: User } } = {};
    logs.forEach((log) => {
        const { user } = log;
        const { id } = user || {};
        if (user && id && !uniqueUsersMap[id]) {
            uniqueUsersMap[id] = { user: user! };
        }
    });

    const uniqueUsers = Object.values(uniqueUsersMap);
    return (
        <UserBubbles
            align="left"
            max={3}
            tooltipPlacement={TooltipPlacement.LEFT}
            users={uniqueUsers}
        />
    );
};

const columns: ColumnDef<AppLogAugmented, string>[] = [
    {
        accessorKey: 'url',
        cell: StringCell,
        enableColumnFilter: false,
        header: Header,
        id: 'url',
        name: 'Common.url',
        size: 300,
        testId: 'url'
    },
    {
        accessorKey: 'count',
        cell: ({ row }) => <UsersCell row={row.original} />,
        enableColumnFilter: false,
        header: Header,
        id: 'count',
        name: 'Common.discovered_users',
        size: 300,
        testId: 'users'
    }
];

/**
 * Parses the given URL string to extract the origin.
 *
 * @param {string} [url] - The URL to be parsed.
 * @returns {string} The origin of the given URL, or the original URL if parsing fails.
 */
export const getHostName = (url?: string) => {
    if (!url) return '';
    try {
        const cleanedURL = new URL(url);

        return cleanedURL.origin;
    } catch (error) {
        // If we fail to parse the URL, return the original URL.
        return url;
    }
};

const handleRows = (rows: AppLog[]) => {
    if (!rows || rows.length === 0) return [];
    let data;
    data = rows
        .filter((row) => row.metadata.url)
        .map((row) => {
            const { metadata, user } = row;
            const url = getHostName(metadata.url);

            return { metadata: { ...metadata, url }, user };
        });

    const urlUserMap: {
        [key: string]: Array<{
            metadata: Metadata;
            user?: User;
        }>;
    } = {};

    data.forEach((d) => {
        const { url } = d.metadata;
        if (url && url !== 'null' && urlUserMap[url]) {
            urlUserMap[url].push(d);
        } else if (url && url !== 'null') {
            urlUserMap[url] = [d];
        }
    });

    data = Object.entries(urlUserMap).map(([key, value]) => ({
        count: value.length,
        logs: value,
        url: key
    }));

    return data;
};

const CloudActivityTable = ({ logs }: { logs: AppLog[] }) => {
    const rows = useMemo(() => handleRows(logs), [logs]);

    return (
        <ManagedTableV2ContextProvider
            columns={columns}
            getRowId="url"
            ignoreSearchParams
            mode="client"
            rows={rows}
            rowsPerPage={10}
            searchEnabled
            sortDirection="DESC"
            sortKey="count"
            tableName={TABLENAMES.USER_APP_ACTIVITY}
        >
            <Paper elevation={0} variant="outlined" sx={{ p: 0 }}>
                <ManagedTableV2UI
                    renderFooter
                    titleDef={{ titleKey: 'Applications.identified_urls' }}
                />
            </Paper>
        </ManagedTableV2ContextProvider>
    );
};

export const UserApplicationActivityTable = ({
    applicationId
}: {
    applicationId: string;
}) => {
    const { clientId } = useParams();

    const { loading, logs } = useFetchTenantApplicationLogs({
        applicationId,
        clientId
    });

    if (loading) {
        return (
            <Box
                data-testid="managedTableRenderLoading"
                display="flex"
                justifyContent="center"
                width="100%"
                className="no-print"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box data-testid="userApplicationActivityContainer">
            <CloudActivityTable logs={logs} />
        </Box>
    );
};
